import classnames from 'classnames'
import * as React from 'react'
import {useCallback, useEffect, useState} from 'react'
import {createPortal} from 'react-dom'
import DSToast from '~/design-system/DSToast'
import {useDispatch, useSelector} from '~/redux/hooks'
import type {TToast} from '~/redux/reducers/toast'
import {dismissToast} from '~/redux/reducers/toast'
import styles from './styles.module.css'

const MAX_VISIBLE_TOASTS = 3

const ToastItem = ({toast}: {toast: TToast}) => {
  const dispatch = useDispatch()
  const [leaving, setLeaving] = useState(false)

  const beginExit = useCallback(() => setLeaving(true), [])

  useEffect(() => {
    if (toast.duration === 'infinite') {
      return
    }
    const timer = window.setTimeout(beginExit, toast.duration)
    return () => window.clearTimeout(timer)
  }, [toast.duration, beginExit])

  return (
    <div
      className={classnames(styles.item, leaving ? styles.leaving : styles.entering)}
      onAnimationEnd={e => {
        // ignore animationend bubbling up from any descendant
        if (e.target === e.currentTarget && leaving) {
          dispatch(dismissToast(toast.id))
        }
      }}
    >
      <DSToast type={toast.type} message={toast.message} onDismiss={beginExit} />
    </div>
  )
}

/**
 * Renders the toast queue held in Redux, portaled above all page content and centered at the top
 * of the viewport. Rendered once in `BaseLayout`.
 */
const ReduxToast = () => {
  const toasts = useSelector(state => state.toastState.toasts)

  if (toasts.length === 0) {
    return null
  }

  return createPortal(
    <div className={styles.container}>
      {toasts.slice(0, MAX_VISIBLE_TOASTS).map(toast => (
        // keyed on the message too, so replacing a toast in place (same id, new
        // message) remounts with a fresh timer and enter animation
        <ToastItem key={`${toast.id}:${toast.message}`} toast={toast} />
      ))}
    </div>,
    document.body
  )
}

export default ReduxToast
